import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search,
  Filter,
  X,
  Download,
  TrendingUp,
  TrendingDown,
  Phone,
  Mail,
  FileText,
  Eye,
  Target,
  ArrowRight,
  Scale,
  CreditCard,
  UserCheck,
  ChevronDown,
} from "lucide-react";
import { type CustomerAgeingData, type AgeingAnalysisFilters, type SmartActionsRecommendation } from "@/types/ageing-analysis";
import { generateMockCustomers, generateAgeingMetrics, generateSmartActions, generateBulkSmartActions } from "@/lib/mock-data/ageing-analysis-mock-data";
import { AgeingAnalysisTable } from "@/components/collections/ageing-analysis-table";
import { AdvancedFiltersSheet } from "@/components/collections/advanced-filters-sheet";
import { ColumnsSheet } from "@/components/collections/columns-sheet";
import { SmartActionsDrawer } from "@/components/collections/smart-actions-drawer";
import { LogCallDialog, SendEmailDialog, CreatePromiseDialog, AssignClerkDialog } from "@/components/collections/ageing-action-dialogs";
import { toast } from "sonner";

type SortField = "customerName" | "totalOutstanding" | "current" | "days31_60" | "days61_90" | "days91_120" | "over120" | "worstDaysOverdue" | "lastContactDate" | "riskLevel" | "aiPriorityScore" | "collectionStrategy";
type SortDirection = "asc" | "desc" | null;

export default function AgeingAnalysisPage() {
  const [customers] = useState<CustomerAgeingData[]>(generateMockCustomers());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>("aiPriorityScore");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  
  // Filter states
  const [filters, setFilters] = useState<AgeingAnalysisFilters>({
    searchQuery: "",
    riskLevel: [],
    daysOverdueMin: null,
    daysOverdueMax: null,
    outstandingMin: null,
    outstandingMax: null,
    collectionStrategy: [],
    hasDisputes: null,
    hasPromisesToPay: null,
  });
  
  // UI states
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [columnsSheetOpen, setColumnsSheetOpen] = useState(false);
  const [smartActionsOpen, setSmartActionsOpen] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [createPromiseOpen, setCreatePromiseOpen] = useState(false);
  const [assignClerkOpen, setAssignClerkOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState<CustomerAgeingData | null>(null);
  const [smartRecommendation, setSmartRecommendation] = useState<SmartActionsRecommendation | null>(null);
  
  // Temp filter states for sheet
  const [tempFilters, setTempFilters] = useState<AgeingAnalysisFilters>(filters);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    totalOutstanding: true,
    current: true,
    days31_60: true,
    days61_90: true,
    days91_120: true,
    over120: true,
    worstDaysOverdue: true,
    lastContactDate: true,
    riskLevel: true,
    aiPriorityScore: true,
    collectionStrategy: true,
    statusFlags: true,
  });
  
  // Calculate metrics
  const metrics = useMemo(() => generateAgeingMetrics(customers), [customers]);
  
  // Apply filters and search
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !customer.customerName.toLowerCase().includes(query) &&
          !customer.accountNumber.toLowerCase().includes(query) &&
          !customer.contact.toLowerCase().includes(query)
        ) {
          return false;
        }
      }
      
      // Risk level filter
      if (filters.riskLevel.length > 0 && !filters.riskLevel.includes(customer.riskLevel)) {
        return false;
      }
      
      // Days overdue filter
      if (filters.daysOverdueMin !== null && customer.worstDaysOverdue < filters.daysOverdueMin) {
        return false;
      }
      if (filters.daysOverdueMax !== null && customer.worstDaysOverdue > filters.daysOverdueMax) {
        return false;
      }
      
      // Outstanding amount filter
      if (filters.outstandingMin !== null && customer.totalOutstanding < filters.outstandingMin) {
        return false;
      }
      if (filters.outstandingMax !== null && customer.totalOutstanding > filters.outstandingMax) {
        return false;
      }
      
      // Collection strategy filter
      if (filters.collectionStrategy.length > 0 && !filters.collectionStrategy.includes(customer.collectionStrategy)) {
        return false;
      }
      
      // Has disputes filter
      if (filters.hasDisputes !== null && customer.statusFlags.hasDispute !== filters.hasDisputes) {
        return false;
      }
      
      // Has promises filter
      if (filters.hasPromisesToPay !== null && customer.statusFlags.hasPromiseToPay !== filters.hasPromisesToPay) {
        return false;
      }
      
      return true;
    });
  }, [customers, searchQuery, filters]);
  
  // Apply sorting
  const sortedCustomers = useMemo(() => {
    if (!sortField || !sortDirection) return filteredCustomers;
    
    return [...filteredCustomers].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (sortField) {
        case "customerName":
          aValue = a.customerName.toLowerCase();
          bValue = b.customerName.toLowerCase();
          break;
        case "totalOutstanding":
          aValue = a.totalOutstanding;
          bValue = b.totalOutstanding;
          break;
        case "current":
          aValue = a.ageing.current;
          bValue = b.ageing.current;
          break;
        case "days31_60":
          aValue = a.ageing.days31_60;
          bValue = b.ageing.days31_60;
          break;
        case "days61_90":
          aValue = a.ageing.days61_90;
          bValue = b.ageing.days61_90;
          break;
        case "days91_120":
          aValue = a.ageing.days91_120;
          bValue = b.ageing.days91_120;
          break;
        case "over120":
          aValue = a.ageing.over120;
          bValue = b.ageing.over120;
          break;
        case "worstDaysOverdue":
          aValue = a.worstDaysOverdue;
          bValue = b.worstDaysOverdue;
          break;
        case "lastContactDate":
          aValue = new Date(a.lastContactDate).getTime();
          bValue = new Date(b.lastContactDate).getTime();
          break;
                case "riskLevel": {
                  const riskOrder = { critical: 4, high: 3, medium: 2, low: 1 };
                  aValue = riskOrder[a.riskLevel];
                  bValue = riskOrder[b.riskLevel];
                  break;
                }
        case "aiPriorityScore":
          aValue = a.aiPriorityScore;
          bValue = b.aiPriorityScore;
          break;
        case "collectionStrategy":
          aValue = a.collectionStrategy.toLowerCase();
          bValue = b.collectionStrategy.toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredCustomers, sortField, sortDirection]);
  
  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };
  
  // Handle selection
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedCustomerIds(new Set(sortedCustomers.map(c => c.id)));
    } else {
      setSelectedCustomerIds(new Set());
    }
  }, [sortedCustomers]);
  
  const handleSelectCustomer = useCallback((customerId: string, checked: boolean) => {
    const newSelection = new Set(selectedCustomerIds);
    if (checked) {
      newSelection.add(customerId);
    } else {
      newSelection.delete(customerId);
    }
    setSelectedCustomerIds(newSelection);
  }, [selectedCustomerIds]);
  
  // Generate smart actions when selection changes
  useMemo(() => {
    if (selectedCustomerIds.size === 1) {
      const customer = customers.find(c => selectedCustomerIds.has(c.id));
      if (customer) {
        setSmartRecommendation(generateSmartActions(customer));
        setSmartActionsOpen(true);
      }
    } else if (selectedCustomerIds.size > 1) {
      const selectedCustomers = customers.filter(c => selectedCustomerIds.has(c.id));
      setSmartRecommendation(generateBulkSmartActions(selectedCustomers));
      setSmartActionsOpen(true);
    } else {
      setSmartActionsOpen(false);
      setSmartRecommendation(null);
    }
  }, [selectedCustomerIds, customers]);
  
  // Apply filters
  const applyFilters = () => {
    setFilters(tempFilters);
    setFiltersSheetOpen(false);
    toast.success("Filters applied");
  };
  
  // Reset filters
  const resetFilters = () => {
    const emptyFilters: AgeingAnalysisFilters = {
      searchQuery: "",
      riskLevel: [],
      daysOverdueMin: null,
      daysOverdueMax: null,
      outstandingMin: null,
      outstandingMax: null,
      collectionStrategy: [],
      hasDisputes: null,
      hasPromisesToPay: null,
    };
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setSearchQuery("");
    toast.info("Filters cleared");
  };
  
  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.riskLevel.length > 0) count++;
    if (filters.daysOverdueMin !== null || filters.daysOverdueMax !== null) count++;
    if (filters.outstandingMin !== null || filters.outstandingMax !== null) count++;
    if (filters.collectionStrategy.length > 0) count++;
    if (filters.hasDisputes !== null) count++;
    if (filters.hasPromisesToPay !== null) count++;
    return count;
  }, [filters]);
  
  // Remove individual filter
  const removeFilter = (filterType: string) => {
    const newFilters = { ...filters };
    switch (filterType) {
      case "riskLevel":
        newFilters.riskLevel = [];
        break;
      case "daysOverdue":
        newFilters.daysOverdueMin = null;
        newFilters.daysOverdueMax = null;
        break;
      case "outstanding":
        newFilters.outstandingMin = null;
        newFilters.outstandingMax = null;
        break;
      case "strategy":
        newFilters.collectionStrategy = [];
        break;
      case "disputes":
        newFilters.hasDisputes = null;
        break;
      case "promises":
        newFilters.hasPromisesToPay = null;
        break;
    }
    setFilters(newFilters);
    setTempFilters(newFilters);
  };
  
  // Action handlers
  const handleLogCall = (customer: CustomerAgeingData) => {
    setActiveCustomer(customer);
    setLogCallOpen(true);
  };
  
  const handleSendEmail = (customer: CustomerAgeingData) => {
    setActiveCustomer(customer);
    setSendEmailOpen(true);
  };
  
  const handleCreatePromise = (customer: CustomerAgeingData) => {
    setActiveCustomer(customer);
    setCreatePromiseOpen(true);
  };
  
  const handleAssignClerk = (customer: CustomerAgeingData) => {
    setActiveCustomer(customer);
    setAssignClerkOpen(true);
  };
  
  const handleViewCustomer = (customer: CustomerAgeingData) => {
    toast.info(`Navigating to ${customer.customerName} details...`);
  };
  
  const handleExport = (format: string) => {
    toast.success(`Exporting to ${format}...`);
  };
  
  const handleExecuteAction = (actionTitle: string) => {
    toast.success(`Initiating: ${actionTitle}`);
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };
  
  // Get risk badge color
  const getRiskBadgeColor = (risk: string) => {
    switch (risk) {
      case "critical":
        return "bg-red-600 text-white hover:bg-red-700";
      case "high":
        return "bg-orange-600 text-white hover:bg-orange-700";
      case "medium":
        return "bg-yellow-600 text-white hover:bg-yellow-700";
      case "low":
        return "border-green-600 text-green-600 bg-transparent hover:bg-green-50";
      default:
        return "";
    }
  };
  
  // Get priority badge color
  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "bg-red-600 text-white";
      case "high":
        return "bg-orange-600 text-white";
      case "medium":
        return "bg-yellow-600 text-white";
      case "low":
        return "bg-blue-600 text-white";
      default:
        return "";
    }
  };
  
  // Get action icon
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case "call":
        return <Phone className="h-4 w-4" />;
      case "email":
        return <Mail className="h-4 w-4" />;
      case "letter":
        return <FileText className="h-4 w-4" />;
      case "visit":
        return <UserCheck className="h-4 w-4" />;
      case "legal":
        return <Scale className="h-4 w-4" />;
      case "payment_plan":
        return <CreditCard className="h-4 w-4" />;
      case "escalate":
        return <TrendingUp className="h-4 w-4" />;
      case "negotiate":
        return <Target className="h-4 w-4" />;
      default:
        return <ArrowRight className="h-4 w-4" />;
    }
  };
  
  // Get action button text
  const getActionButtonText = (actionType: string) => {
    switch (actionType) {
      case "call":
        return "Make Call";
      case "email":
        return "Send Email";
      case "letter":
        return "Generate Letter";
      case "visit":
        return "Schedule Visit";
      case "legal":
        return "Initiate Legal Action";
      case "payment_plan":
        return "Create Payment Plan";
      case "escalate":
        return "Escalate Case";
      case "negotiate":
        return "Start Negotiation";
      default:
        return "Take Action";
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-none border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Ageing Analysis</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Analytics &amp; Reporting &gt; Ageing Analysis
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {/* Ageing Bucket Metrics - Cumulative Bands (matches Dashboard) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 0-90 days */}
            <Card className="border-l-4 border-l-teal-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  0-90 days
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(metrics.current.amount + metrics.days31_60.amount + metrics.days61_90.amount)}
                </div>
                <div className="flex items-center mt-2 text-sm">
                  <span className="text-muted-foreground">
                    {Math.round(((metrics.current.amount + metrics.days31_60.amount + metrics.days61_90.amount) / 
                    (metrics.current.amount + metrics.days31_60.amount + metrics.days61_90.amount + metrics.days91_120.amount + metrics.over120.amount)) * 100)}% of total
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Over 90 days */}
            <Card className="border-l-4 border-l-orange-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Over 90 days
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(metrics.days91_120.amount + metrics.over120.amount)}
                </div>
                <div className="flex items-center mt-2 text-sm">
                  {(metrics.days91_120.trend + metrics.over120.trend) / 2 < 0 ? (
                    <>
                      <TrendingDown className="h-4 w-4 text-green-600 mr-1" />
                      <span className="text-green-600">{formatPercentage(Math.abs((metrics.days91_120.trend + metrics.over120.trend) / 2))}</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-4 w-4 text-red-600 mr-1" />
                      <span className="text-red-600">{formatPercentage((metrics.days91_120.trend + metrics.over120.trend) / 2)}</span>
                    </>
                  )}
                  <span className="text-muted-foreground ml-1">vs last period</span>
                </div>
              </CardContent>
            </Card>

            {/* Over 180 days */}
            <Card className="border-l-4 border-l-red-400">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Over 180 days
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(metrics.over120.amount)}
                </div>
                <div className="flex items-center mt-2 text-sm">
                  {metrics.over120.trend < 0 ? (
                    <>
                      <TrendingDown className="h-4 w-4 text-green-600 mr-1" />
                      <span className="text-green-600">{formatPercentage(Math.abs(metrics.over120.trend))}</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-4 w-4 text-red-600 mr-1" />
                      <span className="text-red-600">{formatPercentage(metrics.over120.trend)}</span>
                    </>
                  )}
                  <span className="text-muted-foreground ml-1">vs last period</span>
                </div>
              </CardContent>
            </Card>

            {/* Over 365 days */}
            <Card className="border-l-4 border-l-red-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Over 365 days
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(metrics.over120.amount * 0.3)}
                </div>
                <div className="flex items-center mt-2 text-sm">
                  {metrics.over120.trend < 0 ? (
                    <>
                      <TrendingDown className="h-4 w-4 text-green-600 mr-1" />
                      <span className="text-green-600">{formatPercentage(Math.abs(metrics.over120.trend))}</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-4 w-4 text-red-600 mr-1" />
                      <span className="text-red-600">{formatPercentage(metrics.over120.trend)}</span>
                    </>
                  )}
                  <span className="text-muted-foreground ml-1">vs last period</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search Bar */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by customer name, account number, or contact"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setFiltersSheetOpen(true)}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Advanced Filters
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Export
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExport("Excel")}>
                        Export as Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport("CSV")}>
                        Export as CSV
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    variant="outline"
                    onClick={() => setColumnsSheetOpen(true)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Columns
                  </Button>
                </div>
              </div>

              {/* Active Filters */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <span className="text-sm text-muted-foreground">Active filters:</span>
                  
                  {filters.riskLevel.length > 0 && (
                    <Badge variant="secondary" className="gap-1">
                      Risk: {filters.riskLevel.join(", ")}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeFilter("riskLevel")}
                      />
                    </Badge>
                  )}
                  
                  {(filters.daysOverdueMin !== null || filters.daysOverdueMax !== null) && (
                    <Badge variant="secondary" className="gap-1">
                      Days Overdue: {filters.daysOverdueMin || 0}-{filters.daysOverdueMax || "180+"}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeFilter("daysOverdue")}
                      />
                    </Badge>
                  )}
                  
                  {(filters.outstandingMin !== null || filters.outstandingMax !== null) && (
                    <Badge variant="secondary" className="gap-1">
                      Outstanding: {formatCurrency(filters.outstandingMin || 0)}-{formatCurrency(filters.outstandingMax || 500000)}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeFilter("outstanding")}
                      />
                    </Badge>
                  )}
                  
                  {filters.collectionStrategy.length > 0 && (
                    <Badge variant="secondary" className="gap-1">
                      Strategy: {filters.collectionStrategy.join(", ")}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeFilter("strategy")}
                      />
                    </Badge>
                  )}
                  
                  {filters.hasDisputes !== null && (
                    <Badge variant="secondary" className="gap-1">
                      Has Disputes: {filters.hasDisputes ? "Yes" : "No"}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeFilter("disputes")}
                      />
                    </Badge>
                  )}
                  
                  {filters.hasPromisesToPay !== null && (
                    <Badge variant="secondary" className="gap-1">
                      Has Promises: {filters.hasPromisesToPay ? "Yes" : "No"}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeFilter("promises")}
                      />
                    </Badge>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="h-6 text-xs"
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Table - Continued in next part */}
          <AgeingAnalysisTable
            customers={sortedCustomers}
            selectedCustomerIds={selectedCustomerIds}
            visibleColumns={visibleColumns}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onSelectAll={handleSelectAll}
            onSelectCustomer={handleSelectCustomer}
            onLogCall={handleLogCall}
            onSendEmail={handleSendEmail}
            onCreatePromise={handleCreatePromise}
            onAssignClerk={handleAssignClerk}
            onViewCustomer={handleViewCustomer}
            formatCurrency={formatCurrency}
            getRiskBadgeColor={getRiskBadgeColor}
          />
        </div>
      </ScrollArea>

      {/* Advanced Filters Sheet - Continued in separate component */}
      <AdvancedFiltersSheet
        open={filtersSheetOpen}
        onOpenChange={setFiltersSheetOpen}
        filters={tempFilters}
        onFiltersChange={setTempFilters}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      {/* Columns Sheet */}
      <ColumnsSheet
        open={columnsSheetOpen}
        onOpenChange={setColumnsSheetOpen}
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={(cols) => setVisibleColumns(cols as typeof visibleColumns)}
      />

      {/* Smart Actions Drawer */}
      {smartRecommendation && (
        <SmartActionsDrawer
          open={smartActionsOpen}
          onOpenChange={setSmartActionsOpen}
          recommendation={smartRecommendation}
          onExecuteAction={handleExecuteAction}
          formatCurrency={formatCurrency}
          getPriorityBadgeColor={getPriorityBadgeColor}
          getActionIcon={getActionIcon}
          getActionButtonText={getActionButtonText}
        />
      )}

      {/* Action Dialogs */}
      <LogCallDialog
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customer={activeCustomer}
      />

      <SendEmailDialog
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        customer={activeCustomer}
      />

      <CreatePromiseDialog
        open={createPromiseOpen}
        onOpenChange={setCreatePromiseOpen}
        customer={activeCustomer}
      />

      <AssignClerkDialog
        open={assignClerkOpen}
        onOpenChange={setAssignClerkOpen}
        customer={activeCustomer}
      />
    </div>
  );
}
